import React, { createContext, useContext, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Sources, Source } from '../../lib/models/Chat';
import { CitationReference } from './CitationReference';
import {
    buildSourceByNumberMap,
    processCitationsForMarkdown,
} from './citationProcessing';
import './ChatComponents.css';

interface MarkdownContentProps {
    content: string;
    sources?: Sources | Source[] | null;
}

const SourceMapContext = createContext<Map<number, Source>>(new Map());

const MarkdownCite = (props: any) => {
    const sourceByNumber = useContext(SourceMapContext);
    const numbersStr = props['data-numbers'];
    const numbers = numbersStr
        ? String(numbersStr)
            .split(',')
            .map((n: string) => parseInt(n.trim(), 10))
            .filter((n: number) => Number.isFinite(n))
        : [];
    const citationSources = numbers.map(n => sourceByNumber.get(n));
    return <CitationReference numbers={numbers} citationSources={citationSources} />;
};

// Ensure links open in new tab
const MarkdownLink: Components['a'] = ({ node, children, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
        {children}
    </a>
);

// Defined at module level so the component types never change identity. A new `cite` type on a
// re-render remounts CitationReference and closes its hover tooltip before "Read More" can be
// clicked (CallScreen re-renders constantly during a video call).
const MARKDOWN_COMPONENTS: Components = { cite: MarkdownCite, a: MarkdownLink };

export const MarkdownContent: React.FC<MarkdownContentProps> = React.memo(({ content, sources }) => {
    const sourceByNumber = useMemo(() => buildSourceByNumberMap(sources), [sources]);
    const processedContent = useMemo(
        () => processCitationsForMarkdown(content || '', new Set(sourceByNumber.keys())),
        [content, sourceByNumber],
    );

    return (
        <SourceMapContext.Provider value={sourceByNumber}>
            <div className="markdown-content">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={MARKDOWN_COMPONENTS}
                >
                    {processedContent}
                </ReactMarkdown>
            </div>
        </SourceMapContext.Provider>
    );
});
